import { Document, InvalidDocument, ALLOWED_FORMATS, ExportStatus } from './types';

export const getFileExtension = (filename: string): string => {
  const lastDotIndex = filename.toLowerCase().lastIndexOf('.');
  return lastDotIndex === -1 ? '' : filename.toLowerCase().substring(lastDotIndex);
};

export const isValidFormat = (record: Document): boolean => {
  const fileName = record.doc_name || record.filename || '';
  const extension = getFileExtension(fileName);
  return ALLOWED_FORMATS.includes(extension as any);
};

export const getInvalidReason = (record: Document): string => {
  if (!record.client_id) {
    return 'Missing Client ID';
  }
  if (!isValidFormat(record)) {
    return 'Invalid file format';
  }
  return '';
};

export const validateDocuments = (records: Document[]): {
  validRecords: Document[];
  invalidRecords: InvalidDocument[];
} => {
  const validRecords = records.filter(record =>
    record.client_id && isValidFormat(record)
  );

  const invalidRecords = records.filter(record =>
    !record.client_id || !isValidFormat(record)
  ).map(record => ({
    ...record,
    invalidReason: getInvalidReason(record)
  }));

  return { validRecords, invalidRecords };
};

export const determineSourceType = (records: Document[]): 'Request' | 'Document' => {
  const hasRequestSource = records.some(record =>
    record.source === 'Request' ||
    record.source_documents ||
    record.type === 'Request' ||
    record.request_type
  );

  return hasRequestSource ? 'Request' : 'Document';
};

// UI Helper Functions
export const getStatusBadgeClass = (status: string): string => {
  switch (status) {
    case 'completed':
      return 'badge-success';
    case 'failed':
      return 'badge-danger';
    case 'processing':
      return 'badge-primary';
    case 'pending':
      return 'badge-warning';
    default:
      return 'badge-secondary';
  }
};

export const formatDate = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return 'Invalid date';
  }

  return date.toLocaleString();
};

export const getProgressMessage = (isExporting: boolean, exportStatus?: ExportStatus | null): string => {
  if (!isExporting) return '';

  const statusMessages = {
    pending: 'Export queued for processing...',
    processing: 'Processing files...',
    completed: 'Export completed successfully!',
    failed: 'Export failed',
  };

  if (exportStatus && exportStatus.status) {
    return statusMessages[exportStatus.status] || 'Checking export status...';
  }

  return 'Exporting files to GruntWorx...';
};

// Error Handling Functions
export const getExportErrorMessage = (error: any): string => {
  if (error.response?.status) {
    const { status } = error.response;
    const responseMessage = error.response.data?.message;

    switch (status) {
      case 401:
        return 'Authentication failed. Please check your GruntWorx integration settings.';
      case 403:
        return 'Access forbidden. Please check your GruntWorx API permissions.';
      case 422:
        return responseMessage || 'Export validation failed. Please check your files and try again.';
      case 500:
        return responseMessage || 'An internal error occurred. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'GruntWorx service is temporarily unavailable. Please try again later.';
      default:
        return responseMessage || `Unexpected error (${status}). Please try again.`;
    }
  } else if (error.code === 'ECONNABORTED') {
    return 'Request timed out. Please try again with fewer or smaller files.';
  } else if (error.code === 'NETWORK_ERROR' || error.message?.includes('Network Error')) {
    return 'Network error. Please check your connection and try again.';
  } else if (error.message) {
    return `Export error: ${error.message}`;
  }

  return 'Export failed. Please try again.';
}; 